// src/storage/srsScheduler.ts — SM-2 Spaced Repetition Scheduler (FR-SRS-001..003)
// Implements interval progression [1, 3, 7, 14, 30] days, ease factor adjustments,
// due queue computation, and local-first persistence for Tudor's long-term memory retention.

export const SRS_INTERVALS: readonly number[] = [1, 3, 7, 14, 30] as const;
export const DEFAULT_EASE_FACTOR = 2.5;
export const MIN_EASE_FACTOR = 1.3;
export const DAY_IN_MS = 24 * 60 * 60 * 1000;

export interface SrsReviewLog {
  timestamp: number;
  correct: boolean;
  intervalDays: number;
  easeFactor: number;
}

export interface SrsItem {
  id: string; // topic or question ID
  topic: string;
  domain: string;
  repetitions: number;
  intervalIndex: number;
  intervalDays: number;
  easeFactor: number;
  lastReviewed: number;
  nextReviewDate: number;
  history: SrsReviewLog[];
}

export interface SrsStorageData {
  version: number;
  items: Record<string, SrsItem>;
  lastReviewSessionDate: string; // YYYY-MM-DD
}

const STORAGE_KEY = 'clawey_srs_state_v1';

/** Calculate next interval and ease factor following modified SM-2 for KS2 11+ */
export function calculateNextSrsState(
  current: SrsItem | null,
  isCorrect: boolean,
  now: number = Date.now()
): {
  repetitions: number;
  intervalIndex: number;
  intervalDays: number;
  easeFactor: number;
  nextReviewDate: number;
} {
  const currentEase = current ? current.easeFactor : DEFAULT_EASE_FACTOR;
  const currentReps = current ? current.repetitions : 0;
  const currentIndex = current ? current.intervalIndex : 0;

  if (isCorrect) {
    const nextReps = currentReps + 1;
    const nextIndex = Math.min(currentIndex + 1, SRS_INTERVALS.length - 1);
    const intervalDays = SRS_INTERVALS[nextIndex] ?? 30;
    // Boost ease factor slightly on success, capped at 3.0
    const nextEase = Math.round(Math.min(3.0, currentEase + 0.1) * 100) / 100;
    const nextReviewDate = now + intervalDays * DAY_IN_MS;

    return {
      repetitions: nextReps,
      intervalIndex: nextIndex,
      intervalDays,
      easeFactor: nextEase,
      nextReviewDate,
    };
  } else {
    // Lapse: reset interval back to day 1, decrease ease factor (min 1.3)
    const nextEase = Math.round(Math.max(MIN_EASE_FACTOR, currentEase - 0.2) * 100) / 100;
    const intervalDays = SRS_INTERVALS[0] ?? 1;
    const nextReviewDate = now + intervalDays * DAY_MS_OFFSET(1);

    return {
      repetitions: 0,
      intervalIndex: 0,
      intervalDays,
      easeFactor: nextEase,
      nextReviewDate,
    };
  }
}

function DAY_MS_OFFSET(days: number): number {
  return days * DAY_IN_MS;
}

export class SrsScheduler {
  /** Load all SRS records from local-first storage */
  static load(): SrsStorageData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return {
          version: 1,
          items: {},
          lastReviewSessionDate: '',
        };
      }
      const parsed = JSON.parse(raw) as Partial<SrsStorageData>;
      return {
        version: 1,
        items: parsed.items && typeof parsed.items === 'object' ? parsed.items : {},
        lastReviewSessionDate: typeof parsed.lastReviewSessionDate === 'string' ? parsed.lastReviewSessionDate : '',
      };
    } catch (err) {
      console.warn('Failed to load SRS state from storage:', err);
      return {
        version: 1,
        items: {},
        lastReviewSessionDate: '',
      };
    }
  }

  /** Save all SRS records to storage */
  static save(data: SrsStorageData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to save SRS state to storage:', err);
    }
  }

  /** Reset all SRS data (useful for testing or profile reset) */
  static clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  /** Get or initialize an SRS item */
  static getItem(id: string): SrsItem | null {
    const data = this.load();
    return data.items[id] ?? null;
  }

  /** Record a review attempt for a topic or question */
  static recordReview(params: {
    id: string;
    topic: string;
    domain: string;
    correct: boolean;
    now?: number;
  }): SrsItem {
    const now = params.now ?? Date.now();
    const data = this.load();
    const existing = data.items[params.id] ?? null;

    const nextState = calculateNextSrsState(existing, params.correct, now);

    const log: SrsReviewLog = {
      timestamp: now,
      correct: params.correct,
      intervalDays: nextState.intervalDays,
      easeFactor: nextState.easeFactor,
    };

    const history = existing ? [...existing.history, log] : [log];

    const updatedItem: SrsItem = {
      id: params.id,
      topic: params.topic,
      domain: params.domain,
      repetitions: nextState.repetitions,
      intervalIndex: nextState.intervalIndex,
      intervalDays: nextState.intervalDays,
      easeFactor: nextState.easeFactor,
      lastReviewed: now,
      nextReviewDate: nextState.nextReviewDate,
      history,
    };

    data.items[params.id] = updatedItem;
    this.save(data);
    return updatedItem;
  }

  /**
   * Get all items currently due for review.
   * Priority:
   * 1. Overdue duration descending (most overdue first)
   * 2. Repetition count ascending (newer/struggling items first)
   * 3. Ease factor ascending (harder items first)
   * Capped to `limit` (default 5 for Tudor's ADHD focus, FR-SRS-002).
   */
  static getDueItems(now: number = Date.now(), limit: number = 5): SrsItem[] {
    const data = this.load();
    const all = Object.values(data.items);

    const due = all.filter((item) => item.nextReviewDate <= now);

    due.sort((a, b) => {
      // 1. Most overdue first
      const overdueA = now - a.nextReviewDate;
      const overdueB = now - b.nextReviewDate;
      if (overdueA !== overdueB) {
        return overdueB - overdueA;
      }
      // 2. Fewer repetitions first
      if (a.repetitions !== b.repetitions) {
        return a.repetitions - b.repetitions;
      }
      // 3. Lower ease factor first
      return a.easeFactor - b.easeFactor;
    });

    return due.slice(0, Math.max(1, limit));
  }

  /** Check if a Daily Review is currently needed */
  static hasDueItems(now: number = Date.now()): boolean {
    return this.getDueItems(now, 1).length > 0;
  }

  /** Count total due items without capping */
  static getDueCount(now: number = Date.now()): number {
    const data = this.load();
    return Object.values(data.items).filter((item) => item.nextReviewDate <= now).length;
  }

  /** Mark today's daily review session as completed */
  static markSessionCompleted(todayIsoDate: string = new Date().toISOString().split('T')[0] ?? ''): void {
    const data = this.load();
    data.lastReviewSessionDate = todayIsoDate;
    this.save(data);
  }

  /** Check if daily review session was already completed today */
  static isSessionCompletedToday(todayIsoDate: string = new Date().toISOString().split('T')[0] ?? ''): boolean {
    const data = this.load();
    return data.lastReviewSessionDate === todayIsoDate;
  }

  /** Quick summary for parent dashboard or telemetry */
  static getTelemetry(): {
    totalTracked: number;
    dueCount: number;
    masteredCount: number; // items at 30-day interval
    averageEaseFactor: number;
  } {
    const data = this.load();
    const items = Object.values(data.items);
    const now = Date.now();

    if (items.length === 0) {
      return {
        totalTracked: 0,
        dueCount: 0,
        masteredCount: 0,
        averageEaseFactor: DEFAULT_EASE_FACTOR,
      };
    }

    const dueCount = items.filter((i) => i.nextReviewDate <= now).length;
    const masteredCount = items.filter((i) => i.intervalDays >= 30).length;
    const avgEase = items.reduce((acc, curr) => acc + curr.easeFactor, 0) / items.length;

    return {
      totalTracked: items.length,
      dueCount,
      masteredCount,
      averageEaseFactor: Math.round(avgEase * 100) / 100,
    };
  }
}
